/**
 * Newick format validation.
 *
 * The vendored tree parser never throws: handed a CSV, an HTML error page, or a
 * truncated tree it returns something empty and the canvas renders blank. This
 * module is the guard in front of it, and reports where the text stops being a
 * tree so the user can fix the file.
 *
 * Grammar accepted:
 *
 *     tree      := subtree ";"
 *     subtree   := leaf | internal
 *     leaf      := name? length?
 *     internal  := "(" subtree ("," subtree)* ")" name? length?
 *     length    := ":" number
 *
 * Square-bracket comments are skipped wherever whitespace is allowed, which
 * covers the NHX annotations written by RAxML and IQ-TREE. Labels may be
 * single-quoted, with '' for a literal quote.
 */

const DELIMITERS = new Set(['(', ')', ',', ':', ';', '[', ']', "'"]);

export interface NewickValidation {
  valid: boolean;
  /** User-facing reason the text was rejected, null when valid. */
  error: string | null;
  /** Number of leaf nodes, 0 when invalid. Counts unlabelled leaves too. */
  tipCount: number;
  /** Leaf labels in file order, empty when invalid. Unlabelled leaves are omitted. */
  tipLabels: string[];
}

class NewickSyntaxError extends Error {}

class Scanner {
  private readonly text: string;
  private pos = 0;
  readonly tipLabels: string[] = [];
  leafCount = 0;

  constructor(text: string) {
    this.text = text;
  }

  /** Character offset translated into the 1-based line and column a user sees. */
  private describePosition(): string {
    const consumed = this.text.slice(0, Math.min(this.pos, this.text.length));
    const line = consumed.split('\n').length;
    const column = consumed.length - consumed.lastIndexOf('\n');
    return `line ${line}, column ${column}`;
  }

  fail(what: string): never {
    const found = this.pos >= this.text.length
      ? 'the end of the file'
      : `"${this.text[this.pos]}"`;
    throw new NewickSyntaxError(`${what} at ${this.describePosition()}, found ${found}.`);
  }

  private skipComment(): void {
    const start = this.pos;
    this.pos++; // consume [
    while (this.pos < this.text.length && this.text[this.pos] !== ']') this.pos++;
    if (this.pos >= this.text.length) {
      this.pos = start;
      this.fail('Unclosed "[" comment');
    }
    this.pos++; // consume ]
  }

  skipIgnorable(): void {
    for (;;) {
      while (this.pos < this.text.length && /\s/.test(this.text[this.pos]!)) this.pos++;
      if (this.text[this.pos] === '[') {
        this.skipComment();
        continue;
      }
      return;
    }
  }

  peek(): string | undefined {
    return this.text[this.pos];
  }

  atEnd(): boolean {
    return this.pos >= this.text.length;
  }

  consume(char: string): void {
    if (this.text[this.pos] !== char) this.fail(`Expected "${char}"`);
    this.pos++;
  }

  private readQuotedName(): string {
    this.pos++; // consume opening quote
    let value = '';
    for (;;) {
      if (this.pos >= this.text.length) this.fail('Unclosed quoted label');
      if (this.text[this.pos] === "'") {
        // '' inside a quoted label is a literal quote, not the end of it.
        if (this.text[this.pos + 1] === "'") {
          value += "'";
          this.pos += 2;
          continue;
        }
        this.pos++;
        return value;
      }
      value += this.text[this.pos];
      this.pos++;
    }
  }

  /** Reads an optional node label. Returns an empty string when there is none. */
  readName(): string {
    if (this.peek() === "'") return this.readQuotedName();

    let value = '';
    while (this.pos < this.text.length) {
      const char = this.text[this.pos]!;
      if (DELIMITERS.has(char) || char === '\n' || char === '\r') break;
      value += char;
      this.pos++;
    }
    // Underscores are the Newick encoding of a space, but the renderer does not
    // decode them. Labels are returned exactly as written so that anything
    // matching them against tree nodes agrees with what is drawn.
    return value.trim();
  }

  readOptionalLength(): void {
    this.skipIgnorable();
    if (this.peek() !== ':') return;
    this.pos++;
    this.skipIgnorable();

    const remainder = this.text.slice(this.pos);
    const match = /^[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?/.exec(remainder);
    if (!match) this.fail('Expected a branch length number after ":"');
    this.pos += match[0].length;
  }

  parseSubtree(depth: number): void {
    // Guards against a pathological file exhausting the JS stack, which would
    // surface as a crash rather than a validation message.
    if (depth > 5000) this.fail('Tree is nested too deeply');

    this.skipIgnorable();

    if (this.peek() === '(') {
      this.consume('(');
      for (;;) {
        this.parseSubtree(depth + 1);
        this.skipIgnorable();
        if (this.peek() === ',') {
          this.pos++;
          continue;
        }
        break;
      }
      this.skipIgnorable();
      if (this.peek() !== ')') this.fail('Expected "," or ")"');
      this.consume(')');
      this.readName();
      this.readOptionalLength();
      return;
    }

    this.leafCount++;
    const label = this.readName();
    if (label) this.tipLabels.push(label);
    this.readOptionalLength();
  }

  parseTree(): void {
    this.skipIgnorable();
    if (this.atEnd()) throw new NewickSyntaxError('The file is empty.');

    this.parseSubtree(0);
    this.skipIgnorable();

    if (this.peek() !== ';') this.fail('Expected ";" to end the tree');
    this.consume(';');

    this.skipIgnorable();
    if (!this.atEnd()) {
      throw new NewickSyntaxError(
        `Extra content after the end of the tree at ${this.describePosition()}. `
          + 'Files containing more than one tree are not supported; keep a single tree.'
      );
    }
  }
}

/**
 * Checks that `text` is a single well-formed Newick tree.
 *
 * Structural failures are reported with the line and column where parsing
 * stopped. A tree that parses but has no leaves is rejected too, because it
 * renders as an empty canvas.
 */
export function validateNewick(text: string): NewickValidation {
  const invalid = (error: string): NewickValidation => ({
    valid: false,
    error,
    tipCount: 0,
    tipLabels: [],
  });

  if (!text || !text.trim()) {
    return invalid('The file is empty.');
  }

  if (text.includes('\0')) {
    return invalid('This looks like a binary file, not a Newick tree.');
  }

  // Checked before parsing so a fetched error page gets a message about what it
  // actually is, rather than a syntax error on its first angle bracket.
  const head = text.trim().slice(0, 200).toLowerCase();
  if (head.startsWith('<')) {
    const kind = head.startsWith('<?xml') ? 'an XML document' : 'an HTML page';
    return invalid(`This is ${kind}, not a Newick tree. Check that the URL points at the tree file itself.`);
  }

  const scanner = new Scanner(text);
  try {
    scanner.parseTree();
  } catch (error) {
    if (error instanceof NewickSyntaxError) return invalid(error.message);
    throw error;
  }

  if (scanner.tipLabels.length === 0) {
    return invalid('No labelled tips were found, so there would be nothing to display.');
  }

  return {
    valid: true,
    error: null,
    tipCount: scanner.leafCount,
    tipLabels: scanner.tipLabels,
  };
}
